import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { selectToken } from "@/store/reducers/member/member.selector";
import { apiPatchLocation } from "@/features/profile/api/profile.api";

const WEB_LOCATION_PERMISSION_HINT_KEY = "zs_web_location_permission_granted";
const WEB_LOCATION_AUTO_PROMPT_SESSION_KEY =
  "zs_web_location_auto_prompted_this_session";
const WEB_LOCATION_AUTO_RETRY_AT_KEY = "zs_web_location_auto_retry_at";
// Sync interval: 5 minutes
const SYNC_INTERVAL_MS = 5 * 60 * 1000;
// Movement threshold to trigger a PATCH: 5 km
const MOVEMENT_THRESHOLD_KM = 5;
// maximumAge passed to the browser: 5 minutes
const GPS_MAX_AGE_MS = 5 * 60 * 1000;
const AUTO_GPS_RETRY_BACKOFF_MS = 2 * 60 * 1000;

type TGeolocationPermissionState =
  | PermissionState
  | "unsupported"
  | "unknown";

const hasLocationPermissionHint = () => {
  try {
    return localStorage.getItem(WEB_LOCATION_PERMISSION_HINT_KEY) === "1";
  } catch {
    return false;
  }
};

const rememberLocationPermissionHint = () => {
  try {
    localStorage.setItem(WEB_LOCATION_PERMISSION_HINT_KEY, "1");
  } catch {
    // Ignore storage issues.
  }
};

const clearLocationPermissionHint = () => {
  try {
    localStorage.removeItem(WEB_LOCATION_PERMISSION_HINT_KEY);
  } catch {
    // Ignore storage issues.
  }
};

const shouldAttemptAutoPrompt = () => {
  try {
    return sessionStorage.getItem(WEB_LOCATION_AUTO_PROMPT_SESSION_KEY) !== "1";
  } catch {
    return true;
  }
};

const markAutoPromptAttempt = () => {
  try {
    sessionStorage.setItem(WEB_LOCATION_AUTO_PROMPT_SESSION_KEY, "1");
  } catch {
    // Ignore storage issues.
  }
};

const getAutoRetryAt = () => {
  try {
    const rawValue = localStorage.getItem(WEB_LOCATION_AUTO_RETRY_AT_KEY);
    const retryAt = rawValue ? Number(rawValue) : 0;

    if (!retryAt || Number.isNaN(retryAt)) {
      return 0;
    }

    return retryAt;
  } catch {
    return 0;
  }
};

const setAutoRetryBackoff = (delayMs: number) => {
  try {
    localStorage.setItem(
      WEB_LOCATION_AUTO_RETRY_AT_KEY,
      String(Date.now() + delayMs)
    );
  } catch {
    // Ignore storage issues.
  }
};

const clearAutoRetryBackoff = () => {
  try {
    localStorage.removeItem(WEB_LOCATION_AUTO_RETRY_AT_KEY);
  } catch {
    // Ignore storage issues.
  }
};

const getRemainingAutoRetryMs = () => Math.max(getAutoRetryAt() - Date.now(), 0);

const isAutoRetryCoolingDown = () => getRemainingAutoRetryMs() > 0;

/** Haversine distance in km between two lat/lng points */
function distanceKm(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Silently checks the geolocation permission state.
 * Returns true only if the permission is already "granted" — never prompts.
 */
async function getGeolocationPermissionState(): Promise<TGeolocationPermissionState> {
  if (!navigator.geolocation) return "unsupported";
  if (!navigator.permissions) {
    return hasLocationPermissionHint() ? "granted" : "unknown";
  }

  try {
    const status = await navigator.permissions.query({ name: "geolocation" });
    if (status.state === "granted") {
      rememberLocationPermissionHint();
      return "granted";
    }
    if (status.state === "denied") {
      clearLocationPermissionHint();
    }
    return status.state;
  } catch {
    return hasLocationPermissionHint() ? "granted" : "unknown";
  }
}

function getCurrentPositionSilent(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      timeout: 10000,
      maximumAge: GPS_MAX_AGE_MS,
      enableHighAccuracy: false,
    });
  });
}

/**
 * Global web location auto-sync.
 *
 * Keeps the authenticated user's GPS position reasonably fresh in the DB
 * without requiring them to open the search filter.
 *
 * Rules:
 * - Only runs when the geolocation permission is already "granted".
 * - Never triggers the browser permission prompt.
 * - Sends PATCH /profiles/location only when:
 *     • no coordinates exist in the profile yet, OR
 *     • the user has moved more than MOVEMENT_THRESHOLD_KM km.
 * - Triggers on: mount, tab visibility restored, window focus, every 5 min while visible.
 */
export const useWebLocationAutoSync = () => {
  const token = useSelector(selectToken);

  /**
   * Local reference for the last known coordinates.
   * It is updated only after successful location syncs in the current session.
   * Using a ref avoids stale-closure issues inside interval/event callbacks.
   */
  const lastKnownRef = useRef<{ latitude: number; longitude: number } | null>(null);
  const isCheckingRef = useRef(false);

  useEffect(() => {
    if (!token) return;

    let destroyed = false;

    const syncCurrentPositionIfNeeded = async (current: {
      latitude: number;
      longitude: number;
    }) => {
      const ref = lastKnownRef.current;

      if (ref !== null) {
        const km = distanceKm(
          ref.latitude,
          ref.longitude,
          current.latitude,
          current.longitude
        );
        if (km < MOVEMENT_THRESHOLD_KM) {
          return;
        }
      }

      try {
        await apiPatchLocation(current);
        rememberLocationPermissionHint();
        clearAutoRetryBackoff();
        lastKnownRef.current = current;
      } catch {}
    };

    const handleAutoGpsError = (error: any, sourceLabel: string) => {
      if (error?.code === 1) {
        clearLocationPermissionHint();
        return;
      }

      if (error?.code === 2 || error?.code === 3) {
        setAutoRetryBackoff(AUTO_GPS_RETRY_BACKOFF_MS);
        return;
      }
    };

    const requestPermissionOnEntry = async () => {
      const permissionState = await getGeolocationPermissionState();
      if (destroyed) return;

      if (permissionState === "unsupported") {
        return;
      }

      if (permissionState === "granted") {
        return;
      }

      if (permissionState === "denied") {
        return;
      }

      if (!shouldAttemptAutoPrompt()) {
        return;
      }

      markAutoPromptAttempt();

      try {
        const position = await getCurrentPositionSilent();
        const current = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        };

        rememberLocationPermissionHint();

        await syncCurrentPositionIfNeeded(current);
      } catch (error: any) {
        handleAutoGpsError(error, "la demande initiale");
      }
    };

    const runCheck = async (
      source: "mount" | "visibility" | "focus" | "interval"
    ) => {
      if (destroyed) return;
      if (document.visibilityState !== "visible") return;
      if (isCheckingRef.current) return;

      isCheckingRef.current = true;

      try {
        const permissionState = await getGeolocationPermissionState();
        if (destroyed) return;

        if (permissionState !== "granted") {
          return;
        }

        if (isAutoRetryCoolingDown()) {
          return;
        }

        let position: GeolocationPosition;
        try {
          position = await getCurrentPositionSilent();
        } catch (error: any) {
          handleAutoGpsError(error, `l'auto-sync ${source}`);
          return;
        }

        if (destroyed) return;

        const current = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        };

        await syncCurrentPositionIfNeeded(current);
      } finally {
        isCheckingRef.current = false;
      }
    };

    requestPermissionOnEntry().then(() => {
      if (!destroyed) {
        runCheck("mount");
      }
    });

    // --- Trigger 1: on mount ---
    // --- Trigger 2: tab becomes visible ---
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        runCheck("visibility");
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    // --- Trigger 3: window regains focus ---
    const onFocus = () => runCheck("focus");
    window.addEventListener("focus", onFocus);

    // --- Trigger 4: interval every 5 minutes (only while tab is visible) ---
    const intervalId = setInterval(() => {
      if (document.visibilityState === "visible") {
        runCheck("interval");
      }
    }, SYNC_INTERVAL_MS);

    return () => {
      destroyed = true;
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", onFocus);
      clearInterval(intervalId);
    };
  }, [token]);
};
